import { isGleapStaticFilesHost } from "./RegionHosts";

/**
 * Uploaded attachments live on the `staticfiles.*` CDN host of the project's
 * region (staticfiles.gleap.io, staticfiles.<region>.gleap.ai). Cloudflare
 * Image Resizing is enabled on it, which is the only reason the rewrite below
 * works — a URL pointing anywhere else is handed back untouched. The transform
 * path is always built on the file's OWN origin, so a regional file is resized
 * by its regional zone.
 */

const RESIZE_PATH_PREFIX = "/cdn-cgi/image/";

/**
 * The HEIF family, matched by extension and by declared MIME type. The type
 * covers `image/heic`, `image/heif` and their `-sequence` variants; the
 * extension is the fallback for uploads that arrived without a usable type.
 */
const HEIF_EXTENSION_PATTERN = /\.(heic|heif)$/i;
const HEIF_MIME_PATTERN = /^image\/hei[cf]/i;

export interface DisplayableAttachment {
  url?: string;
  name?: string;
  type?: string;
}

export const isHeifAttachment = (
  attachment?: DisplayableAttachment
): boolean =>
  HEIF_EXTENSION_PATTERN.test(attachment?.name ?? "") ||
  HEIF_MIME_PATTERN.test(attachment?.type ?? "");

/**
 * The URL to actually paint an attachment from.
 *
 * HEIF photos (`.heic` / `.heif`) are what phone cameras shoot by default,
 * and no Chromium-based browser can decode them — which includes the Android
 * WebView this widget runs inside. An `<img>` pointed at one just fires
 * `onerror`, so a photo one participant sent shows up broken for the other
 * (Gleap #143294). iOS never surfaced it because WebKit decodes HEIC natively.
 *
 * Cloudflare Image Resizing sits on the CDN zone already and sniffs the real
 * bytes, so routing those attachments through `/cdn-cgi/image/` hands the
 * browser a format it can render, cached at the edge — with no upload-side
 * conversion, and working retroactively on every HEIC already stored.
 *
 * Deliberately scoped to HEIF and nothing else. Every other format renders
 * natively, transforms are billed per unique variant, and a PNG that already
 * works is not worth paying to rewrite.
 *
 * Note this is for DISPLAY only — opening an attachment full-screen hands the
 * original url to the host app, which has its own (native) decoders.
 */
export const attachmentDisplayUrl = (
  attachment: DisplayableAttachment | undefined,
  width: number
): string | undefined => {
  const url = attachment?.url;
  if (!url || !isHeifAttachment(attachment)) return url;

  try {
    const parsed = new URL(url);
    // Only our own CDN hosts have the transform enabled, and re-wrapping an
    // already-wrapped URL would nest the options path.
    if (!isGleapStaticFilesHost(parsed.hostname)) return url;
    if (parsed.pathname.startsWith(RESIZE_PATH_PREFIX)) return url;

    // `format=auto` lets the edge pick AVIF/WebP from the request's Accept
    // header and fall back to JPEG; `fit=scale-down` never upscales, so a
    // small source keeps its own dimensions and the aspect ratio is preserved
    // either way.
    return `${parsed.origin}${RESIZE_PATH_PREFIX}format=auto,width=${width},fit=scale-down${parsed.pathname}${parsed.search}`;
  } catch (_) {
    // Not an absolute URL — nothing safe to rewrite.
    return url;
  }
};
